import dayjs from 'dayjs';
import uuid from 'react-native-uuid';

import type { InhouseLog, LogParam } from './types';
import { logService } from 'shared/src/api/logService';
import { isApp } from 'shared/src/utils';

const isStorybook = typeof window !== 'undefined' && window.location?.hostname === 'localhost' && '__STORYBOOK_PREVIEW__' in window;

class InhouseLogger {
  private buffer: InhouseLog[] = [];
  private maxBufferLength = 30;
  private maxBufferTime = 5000;
  private timeoutId: number | undefined;

  constructor() {
    if (isApp() || isStorybook) {
      return;
    }
    this._scheduleFlush();

    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });
  }

  public log({ eventName, analytics, ablyOnlyLog = {} }: LogParam) {
    if (isStorybook) {
      console.log(`[InhouseLogger] ${eventName}`, analytics);
      return;
    }
    this.buffer.push({
      analytics: {
        ANALYTICS_EVENT_NAME: eventName,
        ...analytics,
      },
      ably_only_log: {
        ...ablyOnlyLog,
      },
      common: {
        log_id: uuid.v4(),
        occurred_at: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
      },
    });

    if (this.buffer.length < this.maxBufferLength) {
      return;
    }

    this.flush();
  }

  public async flush() {
    if (this?.buffer?.length === 0) {
      return;
    }

    const buffer = this.buffer;
    this.buffer = [];

    clearTimeout(this.timeoutId);

    try {
      await logService.postLog({
        clientTime: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
        events: buffer,
      });
    } catch {
      // 전송 실패는 삼킨다. 여기서 throw하면 주기 flush가 재예약되지 않아 이후 로그가
      // 버퍼에 갇히고, 장애 시에는 5초마다 unhandled rejection이 쌓인다.
    } finally {
      this._scheduleFlush();
    }
  }

  private _scheduleFlush() {
    // flush가 겹치면 인터벌이 중복 생성돼 clear할 수 없게 되므로 항상 이전 것을 지운다.
    clearTimeout(this.timeoutId);
    // eslint-disable-next-line no-restricted-syntax -- 웹 전용 로거 (isApp()이면 early return)
    this.timeoutId = window.setInterval(() => {
      this.flush();
    }, this.maxBufferTime);
  }
}

export const inhouseLogger = new InhouseLogger();
